// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import FancyScroll from "@adl/App/Components/Common/FancyScroll";
import { getCartLineErrorMessages } from "@adl/App/Components/MiniCart/MiniCart.helpers";
import MiniCartLineCard from "@adl/App/Components/MiniCart/MiniCartLineCard";
import Notify from "@adl/App/Components/Notify/Notify";
import { mobileTippyBreakpoint } from "@adl/App/Components/Tippy/Style";
import { CustomAppState } from "@adl/App/Store/Reducers";
import { getErpSystemStatus } from "@adl/App/Store/Selectors/CartSelectors";
import { color } from "@adl/XDL/Utilities";
import { xScreen } from "@adl/XDL/Utilities";
import { styledScrollbar } from "@adl/XDL/Utilities";
import { isCheckoutDisabledHandler } from "@adl/XDL/Utilities/helpers";
import { mergeWithCss } from "@adl/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Logger from "@insite/client-framework/Logger";
import {
    canSubmitRequisition,
    getCurrentCartState,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export const cartLineTippyContentTopPadding = 20;

interface OwnProps {
    isPopupShown?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const cart = getCurrentCartState(state).value;
    // <XNG-Changes>
    const { erpSystemIsDown } = getErpSystemStatus(state, cart?.id);
    // </XNG-Changes>
    const isCheckoutDisabled = isCheckoutDisabledHandler(cart) || erpSystemIsDown;
    const currentPage = getCurrentPage(state);
    
    return {
        isCheckoutDisabled,
        canSubmitRequisition: canSubmitRequisition(cart),
        isPunchOutOrder: isPunchOutOrder(cart),
        shouldLoadCart:
            !getCurrentCartState(state).value &&
            !getCurrentCartState(state).isLoading &&
            currentPage.type !== "CartPage",
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay || 0,
        orderSubTotalDisplay: getCurrentCartState(state).value?.orderSubTotalDisplay,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        cart,
        isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
        isLoading: state.data.carts.isLoading,
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
    updateCartLine,
    removeCartLine,
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    submitRequisition,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

interface MiniCartStyles {
    wrapper?: InjectableCss;
    centerWrapper?: InjectableCss;
    notifyWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyProps;
    totalItems?: TypographyProps;
    noCartLinesNotify?: TypographyProps;
    viewCartLink?: LinkPresentationProps;
    cartLinesWrapper?: InjectableCss;
    brandName?: TypographyProps;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    actionWrapper?: InjectableCss;
    subTotalWrapper?: InjectableCss;
    subTotalTitle?: TypographyProps;
    subTotalValue?: TypographyProps;
    checkoutButton?: ButtonPresentationProps;
    viewCartButton?: ButtonPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
}

const miniCartStyles: MiniCartStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            max-width: 660px;
            min-width: 250px;
            ${xScreen("lte", mobileTippyBreakpoint)} {
                width: 100%;
                max-width: initial;
                justify-content: space-between;
            }
        `,
    },
    title: {
        size: 18,
        weight: "bold",
        as: "p",
        css: css`
            letter-spacing: 0;
            line-height: 22px;
            margin-bottom: 12px;
        `,
    },
    centerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            max-width: 660px;
            min-width: 250px;
            ${xScreen("lte", mobileTippyBreakpoint)} {
                width: 100%;
                max-width: initial;
                justify-content: space-between;
            }
        `,
    },
    notifyWrapper: {
        css: css`
            padding-bottom: 45px;
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            margin: 18px 0;
            padding: 0 16px;
            align-items: center;
            ${xScreen("lte", mobileTippyBreakpoint)} {
                margin-top: 18px;
                margin-bottom: 16px;
            }
        `,
    },
    totalItems: {
        size: 15,
        weight: "normal",
        css: css`
            letter-spacing: 0;
            line-height: 26px;
            text-align: right;
            margin-bottom: 0;
            display: flex;
            justify-content: space-between;
            width: 100%;
        `,
    },
    viewCartLink: {
        color: "secondary",
        typographyProps: {
            size: 12,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
            `,
        },
    },
    cartLinesWrapper: {
        css: css`
            position: relative;
            min-height: 100px;
            max-height: 450px;
            padding: 16px;
            border: 1px solid ${color("common.border")};
            width: 615px;
            overflow-x: hidden;
            ${styledScrollbar(8)}

            ${xScreen("lte", mobileTippyBreakpoint)} {
                margin-bottom: 5px;
                padding-right: 0;
                max-height: initial;
                flex-grow: 1;
            }
        `,
    },
    productAvailabilityStyles: {
        container: {
            gap: 0,
            css: css`
                width: auto;
                flex: 1 0 auto;
            `,
        },
    },
    brandName: {
        size: 12,
        weight: "bold",
        as: "p",
        css: css`
            letter-spacing: 0;
            line-height: 18px;
            margin-bottom: 5px;
        `,
    },
    actionWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            position: relative;
            padding: 0 16px;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
            }
        `,
    },
    subTotalTitle: {
        size: 12,
        css: css`
            margin-right: 7px;
            letter-spacing: 0;
            line-height: 18px;
        `,
    },
    subTotalValue: {
        size: 20,
        weight: "bold",
        css: css`
            letter-spacing: 0;
            line-height: 28px;
        `,
    },
    subTotalWrapper: {
        css: css`
            margin-bottom: 17px;
        `,
    },
    checkoutButton: {
        sizeVariant: "medium",
        mergeCss: true,
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
                margin-bottom: 10px;
            }
        `,
    },
    viewCartButton: {
        variant: "secondary",
        sizeVariant: "medium",
        mergeCss: true,
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
                margin-bottom: 10px;
            }
        `,
    },
};

const styles = miniCartStyles;

const MiniCart: FC<Props> = ({
    cart,
    cartPageUrl,
    requisitionConfirmationPageUrl,
    isPunchOutOrder,
    isCheckoutDisabled,
    totalCountDisplay,
    orderSubTotalDisplay,
    removeCartLine,
    updateCartLine,
    submitRequisition,
    preloadCheckoutShippingData,
    canSubmitRequisition,
    doPunchOutCheckout,
    isUpdatingCartLine,
    isLoading,
    checkoutShippingPageUrl,
    isPopupShown,
}) => {
    const history = useHistory();
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const parentBoxRef = React.createRef<HTMLDivElement>();

    const hideTippy = () => {
        setTimeout(() => {
            hideAll();
        }, 500);
    };

    const qtyOrderedChangeHandler = (cartLine: CartLineModel, qtyOrdered: number) => {
        if (qtyOrdered <= 0) {
            removeCartLine({ cartLineId: cartLine.id });
        } else {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered,
                },
            });
        }
    };

    const checkoutHandler = () => {
        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }
        setIsCheckoutLoading(true);
        preloadCheckoutShippingData({
            onSuccess: () => {
                setIsCheckoutLoading(false);
                history.push(checkoutShippingPageUrl!);
            },
        });
    };

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    const removeCartLineClickHandler = (cartLine: CartLineModel) => {
        removeCartLine({ cartLineId: cartLine.id });
    };

    const handleViewCarClick = () => {
        if (!cartPageUrl) {
            Logger.error(`No Cart Page Url found`);
            return;
        }
        hideTippy();
        history.push(cartPageUrl);
    };

    if (!cart && isLoading) {
        return (
            <StyledWrapper {...styles.centerWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!cart) {
        return null;
    }

    const cartLines = cart.cartLines ?? [];
    const isLoadingCart = isLoading[cart.id];
    const isCheckoutButtonDisabled = isCheckoutDisabled || isCheckoutLoading || isLoadingCart;

    if (isLoadingCart) {
        return (
            <StyledWrapper {...styles.centerWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (!cartLines?.length) {
        return (
            <StyledWrapper {...mergeWithCss(styles.wrapper, styles.notifyWrapper)}>
                <Notify {...styles.noCartLinesNotify} type="info">
                    {translate("Add some items to cart!")}
                </Notify>
            </StyledWrapper>
        );
    }

    return (
        <LoadingOverlay {...styles.loadingOverlay} loading={isLoadingCart || isUpdatingCartLine}>
            <Typography {...styles.title}>{translate("Cart Summary")}</Typography>
            <FancyScroll refElement={parentBoxRef} forceUpdate={isPopupShown}>
                <StyledWrapper {...styles.cartLinesWrapper} ref={parentBoxRef} data-test-selector="cartLines-container">
                    {cartLines?.map(cartLine => {
                        const errorMessages = getCartLineErrorMessages(cartLines, cartLine);
                        return (
                            <MiniCartLineCard
                                key={`miniCartLine_${cartLine.id}`}
                                cartLine={cartLine}
                                cart={cart}
                                hideTippy={hideTippy}
                                qtyOrderedChangeHandler={qtyOrderedChangeHandler}
                                removeCartLineClickHandler={removeCartLineClickHandler}
                                errorMessages={errorMessages}
                            />
                        );
                    })}
                </StyledWrapper>
            </FancyScroll>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography {...styles.totalItems}>
                        <Typography>
                            {totalCountDisplay}&nbsp;
                            {translate(`Item${totalCountDisplay > 2 || totalCountDisplay === 0 ? "s" : ""}`)}
                        </Typography>
                        <StyledWrapper>
                            <Typography {...styles.subTotalTitle}>{`${translate("Subtotal")}:`}</Typography>
                            <Typography {...styles.subTotalValue}>{orderSubTotalDisplay}</Typography>
                        </StyledWrapper>
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.actionWrapper}>
                    <Button {...styles.viewCartButton} onClick={handleViewCarClick}>
                        {translate("View My Cart")}
                    </Button>
                    {!canSubmitRequisition ? (
                        <Button
                            {...styles.checkoutButton}
                            onClick={checkoutHandler}
                            disabled={!!isCheckoutButtonDisabled}
                        >
                            {cart?.requiresApproval
                                ? translate("Checkout for Approval")
                                : isPunchOutOrder
                                ? translate("Return to Procurement System")
                                : translate("Proceed to Checkout")}
                        </Button>
                    ) : (
                        <Button
                            {...styles.checkoutButton}
                            onClick={submitRequisitionClickHandler}
                            data-test-selector="cartTotal_SubmitRequisition"
                        >
                            {translate("Submit Requisition")}
                        </Button>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(MiniCart));
